import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import PerformanceMonitor from "@/components/shared/PerformanceMonitor";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { ABTestProvider } from "@/components/shared/ABTestFramework";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import Layout from "@/components/layout/Layout";
import ScrollToTop from "@/components/shared/ScrollToTop";
import ScrollToTopButton from "@/components/shared/ScrollToTopButton";
import { CookieConsent } from "@/components/shared/CookieConsent";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import TrailingSlashRedirect from "@/components/redirects/TrailingSlashRedirect";
import { errorTracking } from "@/lib/errorTracking";
import { usePageTracking } from "@/hooks/useAnalytics";
import { useAuth } from "@/contexts/AuthContext";
import { lazy, Suspense, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";

// Route-based code splitting for performance optimization
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const SupportCenter = lazy(() => import("./pages/admin/SupportCenter"));
const PaymentManagement = lazy(() => import("./pages/admin/PaymentManagement"));
const RefundManagement = lazy(() => import("./pages/admin/RefundManagement"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const SystemSettings = lazy(() => import("./pages/admin/SystemSettings"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const NotFound = lazy(() => import("./pages/NotFound"));
const NoDegreeNavigator = lazy(() => import("./pages/NoDegreeNavigator"));
const Calculators = lazy(() => import("./pages/Calculators"));
const SalaryCalculator = lazy(() => import("./pages/SalaryCalculator"));
const TaxCalculator = lazy(() => import("./pages/TaxCalculator"));
const OccupationSalaryCenter = lazy(() => import("./pages/OccupationSalaryCenter"));
const IndustryCenter = lazy(() => import("./pages/IndustryCenter"));
const IndustryPages = lazy(() => import("./pages/IndustryPages"));
const TrendsCenter = lazy(() => import("./pages/TrendsCenter"));
const NoDegreeJobs = lazy(() => import("./pages/NoDegreeJobs"));
const EntryLevelJobs = lazy(() => import("./pages/EntryLevelJobs"));
const BestStates = lazy(() => import("./pages/BestStates"));
const StateOccupations = lazy(() => import("./pages/StateOccupations"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const OccupationRankings = lazy(() => import("./pages/OccupationRankings"));
const StateRedirect = lazy(() => import("./components/redirects/StateRedirect"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const RefundRequest = lazy(() => import("./pages/RefundRequest"));
const Methodology = lazy(() => import("./pages/Methodology"));
const DataSources = lazy(() => import("./pages/DataSources"));
const SocCodeGuide = lazy(() => import("./pages/SocCodeGuide"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Brand = lazy(() => import("./pages/Brand"));
const ServerError = lazy(() => import("./pages/ServerError"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Support = lazy(() => import("./pages/Support"));
const Insights = lazy(() => import("./pages/Insights"));
const StateOfWages = lazy(() => import("./pages/StateOfWages"));
const SalaryPercentilesGuide = lazy(() => import("./pages/insights/SalaryPercentilesGuide"));
const CostOfLivingVsSalary = lazy(() => import("./pages/insights/CostOfLivingVsSalary"));
const NoDegreeJobsGuide = lazy(() => import("./pages/insights/NoDegreeJobsGuide"));
const UnderstandingWageTrends = lazy(() => import("./pages/insights/UnderstandingWageTrends"));
const SalaryNegotiationGuide = lazy(() => import("./pages/insights/SalaryNegotiationGuide"));
const AIWagePremium = lazy(() => import("./pages/insights/AIWagePremium"));
const AICareerAnalysis = lazy(() => import("./pages/AICareerAnalysis"));
const AIEconomyJobs = lazy(() => import("./pages/AIEconomyJobs"));
const AmIUnderpaid = lazy(() => import("./pages/AmIUnderpaid"));
const IncomePercentile = lazy(() => import("./pages/IncomePercentile"));
const RealWages = lazy(() => import("./pages/RealWages"));
const HighestPayingHub = lazy(() => import("./pages/HighestPayingHub"));
const OccupationsIndex = lazy(() => import("./pages/OccupationsIndex"));
const StatesIndex = lazy(() => import("./pages/StatesIndex"));
const AICareerPlanningResources = lazy(() => import("./pages/AICareerPlanningResources"));
const Resources = lazy(() => import("./pages/Resources"));
const CareerGuide = lazy(() => import("./pages/CareerGuide"));
const OccupationComparison = lazy(() => import("./pages/OccupationComparison"));
const CareerPath = lazy(() => import("./pages/CareerPath"));
const InDemandCareers = lazy(() => import("./pages/InDemandCareers"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NewsletterManagement = lazy(() => import("./pages/admin/NewsletterManagement"));

// Loading component for code-split routes
const PageLoader = () => (
  <div className="container mx-auto px-4 py-8">
    <div className="space-y-4">
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  </div>
);

const queryClient = new QueryClient();

// Sends a GA4 page_view on every route change (must live inside the Router)
const PageTracker = () => {
  usePageTracking();
  return null;
};

// Component to initialize error tracking with user context
const AppContent = () => {
  const { user, profile } = useAuth();

  useEffect(() => {
    // Initialize error tracking
    errorTracking.init();

    // Set user context for error tracking
    if (user) {
      errorTracking.setUser(
        user.id,
        user.email,
        profile?.subscription_status
      );
    } else {
      errorTracking.clearUser();
    }
  }, [user, profile]);

  return (
    <ABTestProvider>
      <TooltipProvider>
      <Toaster />
      <Sonner />
      <PerformanceMonitor />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PageTracker />
        <ScrollToTop />
        <TrailingSlashRedirect />
        <ErrorBoundary>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<Index />} />
                  
                  {/* Authentication Routes */}
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={
                    <ProtectedRoute requiresAdmin>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/users" element={
                    <ProtectedRoute requiresAdmin>
                      <UserManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/support" element={
                    <ProtectedRoute requiresAdmin>
                      <SupportCenter />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/payments" element={
                    <ProtectedRoute requiresAdmin>
                      <PaymentManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/refunds" element={
                    <ProtectedRoute requiresAdmin>
                      <RefundManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/analytics" element={
                    <ProtectedRoute requiresAdmin>
                      <Analytics />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/settings" element={
                    <ProtectedRoute requiresAdmin>
                      <SystemSettings />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/admin-users" element={
                    <ProtectedRoute requiresAdmin>
                      <AdminUsers />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/newsletter" element={
                    <ProtectedRoute requiresAdmin>
                      <NewsletterManagement />
                    </ProtectedRoute>
                  } />
                  
                  {/* No-Degree Navigator Routes */}
                  <Route path="/no-degree-jobs" element={<NoDegreeJobs />} />
                  <Route path="/no-degree-jobs/:location" element={<NoDegreeNavigator />} />
                  
                  {/* Entry-Level Jobs Routes */}
                  <Route path="/best-paying-entry-level" element={<EntryLevelJobs />} />
                  <Route path="/best-paying-entry-level/:location" element={<NoDegreeNavigator />} />
                  
                  {/* Calculator Routes */}
                  <Route path="/calculators" element={<Calculators />} />
                  <Route path="/calculators/salary" element={<SalaryCalculator />} />
                  <Route path="/calculators/salary/:amount" element={<SalaryCalculator />} />
                  <Route path="/calculators/tax" element={<TaxCalculator />} />
                  
                  {/* Legacy redirects - keep old URLs working */}
                  <Route path="/salary-calculator" element={<SalaryCalculator />} />
                  <Route path="/salary-calculator/:amount" element={<SalaryCalculator />} />
                  <Route path="/calculator" element={<SalaryCalculator />} />
                  <Route path="/calculator/:amount" element={<SalaryCalculator />} />
                  
                  {/* Handle common URL patterns where users put state first */}
                  <Route path="/salary/:state" element={<StateRedirect />} />
                  
                  {/* Occupation Salary Center Routes */}
                  <Route path="/salary/:occupation/:location" element={<OccupationSalaryCenter />} />
                  
          {/* Industry Center Routes */}
          <Route path="/industry" element={<IndustryCenter />} />
          <Route path="/industry/:naics/:location" element={<IndustryPages />} />
          <Route path="/industry/:naics" element={<IndustryPages />} />
                  
                  {/* Trends Center Routes */}
                  <Route path="/trends" element={<TrendsCenter />} />
                  <Route path="/trends/:occupation/:location" element={<TrendsCenter />} />
                  
                  {/* Best States Routes */}
                  <Route path="/best-states" element={<BestStates />} />
                  <Route path="/best-states/:occupation" element={<BestStates />} />
                  
                  {/* State-specific occupation rankings */}
                  <Route path="/state/:state" element={<StateOccupations />} />
                  
                  {/* Search Results */}
                  <Route path="/search" element={<SearchResults />} />
                  
                  {/* Occupation Rankings */}
                  <Route path="/rankings" element={<OccupationRankings />} />
                  
                  {/* Payment & Legal Pages */}
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/refund-request" element={
                    <ProtectedRoute>
                      <RefundRequest />
                    </ProtectedRoute>
                  } />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/refund-request" element={
                    <ProtectedRoute>
                      <RefundRequest />
                    </ProtectedRoute>
                  } />
                  <Route path="/methodology" element={<Methodology />} />
                  <Route path="/data-sources" element={<DataSources />} />
                  <Route path="/what-is-soc-code" element={<SocCodeGuide />} />
                  <Route path="/soc-code-guide" element={<SocCodeGuide />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/about/brand" element={<Brand />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/insights" element={<Insights />} />
                  <Route path="/state-of-american-wages" element={<StateOfWages />} />
                  <Route path="/reports/state-of-american-wages" element={<StateOfWages />} />
                  <Route path="/insights/how-to-read-salary-percentiles" element={<SalaryPercentilesGuide />} />
                  <Route path="/insights/cost-of-living-vs-salary" element={<CostOfLivingVsSalary />} />
                  <Route path="/insights/high-paying-jobs-without-a-degree" element={<NoDegreeJobsGuide />} />
                  <Route path="/insights/understanding-wage-trends" element={<UnderstandingWageTrends />} />
                  <Route path="/insights/salary-negotiation-guide" element={<SalaryNegotiationGuide />} />
                  <Route path="/insights/ai-wage-premium" element={<AIWagePremium />} />
                  
                  {/* Flagship free tools */}
                  <Route path="/am-i-underpaid" element={<AmIUnderpaid />} />
                  <Route path="/salary-checker" element={<AmIUnderpaid />} />
                  <Route path="/income-percentile" element={<IncomePercentile />} />
                  <Route path="/real-wages" element={<RealWages />} />
                  <Route path="/in-demand-careers" element={<InDemandCareers />} />

                  {/* AI Career Analysis */}
                  <Route path="/ai-career-analysis" element={<AICareerAnalysis />} />
                  <Route path="/resources/ai-career-planning" element={<AICareerPlanningResources />} />

                  {/* AI Economy Jobs hub (national + state drill-down) */}
                  <Route path="/ai-economy-jobs" element={<AIEconomyJobs />} />
                  <Route path="/ai-economy-jobs/:location" element={<AIEconomyJobs />} />

                  {/* G3 highest-paying comparison hubs + A–Z browse indexes */}
                  <Route path="/highest-paying/:slug" element={<HighestPayingHub />} />
                  <Route path="/occupations" element={<OccupationsIndex />} />
                  <Route path="/states" element={<StatesIndex />} />
                  
                  {/* Content Expansion Pages */}
                  <Route path="/career-guide/:occupation" element={<CareerGuide />} />
                  <Route path="/career-guide/:occupation/:location" element={<CareerGuide />} />
                  <Route path="/compare-occupations" element={<OccupationComparison />} />
                  <Route path="/career-path/:occupation" element={<CareerPath />} />
                  <Route path="/career-path/:occupation/:location" element={<CareerPath />} />
                  
                  {/* Link Building Pages */}
                  <Route path="/resources" element={<Resources />} />
                  
                  {/* Error pages */}
                  <Route path="/error" element={<ServerError />} />
                  
                  {/* Email Management */}
                  <Route path="/unsubscribe" element={<Unsubscribe />} />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                        </Routes>
                      </ErrorBoundary>
                    </Suspense>
                  </Layout>
                </ErrorBoundary>
                
                {/* Scroll to Top Button */}
                <ScrollToTopButton />
                <CookieConsent />
              </BrowserRouter>
            </TooltipProvider>
          </ABTestProvider>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <ThemeProvider 
        attribute="class" 
        defaultTheme="light" 
        enableSystem={false}
        disableTransitionOnChange
      >
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
